import { useEffect, useRef, useState } from 'react';
import { Alert, Button } from '../ds';
import { api, type VibeCheckAttachment, type VibeCheckContext, type VibeCheckMessage, type VibeCheckProposal, type VibeCheckResponse } from '../api';
import { cardStyle, eyebrowStyle } from '../tokens';
import { VibeCheckThinking } from '../components/VibeCheckThinking';
import { AdvisorMessage } from '../components/AdvisorMessage';
import { VibeCheckAttachments } from '../components/VibeCheckAttachments';
import { ATTACHMENT_ACCEPT, attachmentSelectionError, readAttachment } from '../components/vibeCheckAttachmentFiles';
import './vibe-check.css';

const starters = [
  { title: 'Automate a routine task', prompt: 'I want to automate a repetitive task for my team. Help me figure out where to start.' },
  { title: 'Turn data into a dashboard', prompt: 'I have data I want to turn into a dashboard for my team. Can we explore what that would need?' },
  { title: 'Build an internal tool', prompt: 'I have an idea for an internal tool. Help me work out whether it fits Vibes.' },
];

const capabilityDescriptions = [
  { name: 'Background Jobs', key: 'cloudRunJobs' as const, label: 'Handle longer tasks in the background', detail: 'Work can finish while you get on with something else.' },
  { name: 'Scheduled Tasks', key: 'cloudScheduler' as const, label: 'Run tasks automatically', detail: 'Start work at the times you choose, like updating a report each morning.' },
  { name: 'File Storage', key: 'cloudStorage' as const, label: 'Keep files', detail: 'Save uploads and reports so people can use them later.' },
  { name: 'Data Analytics', key: 'bigquery' as const, label: 'Explore larger sets of data', detail: 'Find patterns and calculate totals across lots of information.' },
  { name: 'Transactional Database', key: 'cloudSql' as const, label: 'Remember records and changes', detail: 'Keep track of forms, approvals, and other information your app needs.' },
];

const contextTopics = [
  { key: 'purpose' as const, label: 'The idea' },
  { key: 'people' as const, label: 'Who it’s for' },
  { key: 'information' as const, label: 'The information' },
  { key: 'connections' as const, label: 'Other tools' },
  { key: 'usage' as const, label: 'How it’ll be used' },
];
const emptyContext: VibeCheckContext = { purpose: false, people: false, information: false, connections: false, usage: false };

const introPreferenceKey = 'vibes.vibe-check.hide-intro';

const labels = { exploring: 'Exploring your idea', fits: 'Fits the platform', conditional: 'Fits with conditions', 'outside-scope': 'Outside current scope' };
export function VibeCheck({ active = true, onBack, onUse }: { active?: boolean; onBack?: () => void; onUse: (proposal: VibeCheckProposal) => void }) {
  const activeRef = useRef(active);
  activeRef.current = active;
  const [hideIntro, setHideIntro] = useState(() => {
    try { return window.localStorage.getItem(introPreferenceKey) === 'true'; } catch { return false; }
  });
  const [introDismissed, setIntroDismissed] = useState(hideIntro);
  const rememberIntro = (checked: boolean) => {
    setHideIntro(checked);
    try { window.localStorage.setItem(introPreferenceKey, String(checked)); } catch { /* Keep the toggle usable when storage is unavailable. */ }
  };
  const introDialog = useRef<HTMLDialogElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const attachmentGeneration = useRef(0);
  const readingAttachments = useRef(false);
  const [attachments, setAttachments] = useState<VibeCheckAttachment[]>([]);
  const [reading, setReading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  useEffect(() => () => { attachmentGeneration.current++; }, []);
  useEffect(() => {
    if (!active) {
      introDialog.current?.close?.();
      return;
    }
    if (introDismissed) {
      composer.current?.focus({ preventScroll: true });
      return;
    }
    const dialog = introDialog.current;
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }, [introDismissed, active]);
  const restoreComposerFocus = useRef(false);
  const threadEnd = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<VibeCheckMessage[]>([]);
  const [context, setContext] = useState<VibeCheckContext>(emptyContext);
  const [answer, setAnswer] = useState<VibeCheckResponse | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [retry, setRetry] = useState<VibeCheckMessage[] | null>(null);
  const hasWork = messages.length > 0 || draft.trim().length > 0 || attachments.length > 0 || reading;
  useEffect(() => {
    if (!hasWork) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [hasWork]);
  const attachFiles = async (files: File[]) => {
    if (!files.length || readingAttachments.current || retry || exhausted) return;
    if (files.length + attachments.length > 4) { setAttachmentError('Attach up to 4 files at a time.'); return; }
    if (files.reduce((sum, file) => sum + file.size, 0) > 10 * 1024 * 1024) { setAttachmentError('Keep attachments under 10 MB total per conversation.'); return; }
    readingAttachments.current = true;
    setReading(true); setAttachmentError(null);
    const generation = attachmentGeneration.current;
    try {
      const added = await Promise.all(files.map(readAttachment));
      if (generation !== attachmentGeneration.current) return;
      const selected = [...attachments, ...added];
      const error = attachmentSelectionError(selected, messages.flatMap(message => message.attachments ?? []));
      if (error) throw new Error(error);
      setAttachments(selected);
      if (activeRef.current) composer.current?.focus({ preventScroll: true });
    } catch (error) {
      if (generation === attachmentGeneration.current) setAttachmentError(error instanceof Error ? error.message : 'Could not read the attachment.');
    } finally {
      if (generation === attachmentGeneration.current) { readingAttachments.current = false; setReading(false); }
    }
  };
  const send = async (conversation: VibeCheckMessage[]) => {
    restoreComposerFocus.current = true;
    composer.current?.focus({ preventScroll: true });
    setBusy(true); setError(null); setAnswer(null); setAcknowledged(false);
    setMessages(conversation); setRetry(conversation);
    try {
      const response = await api.vibeCheck(conversation);
      setAnswer(response);
      setContext(response.context);
      setMessages([...conversation, { role: 'assistant', content: JSON.stringify(response) }]);
      setRetry(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not finish the conversation.'); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (active) threadEnd.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages, busy, answer, active]);
  useEffect(() => {
    if (!composer.current) return;
    composer.current.style.height = 'auto';
    composer.current.style.height = `${Math.min(composer.current.scrollHeight, 180)}px`;
  }, [draft]);
  useEffect(() => {
    if (active && !busy && restoreComposerFocus.current) {
      composer.current?.focus({ preventScroll: true });
      restoreComposerFocus.current = false;
    }
  }, [busy, active]);
  const reset = () => {
    attachmentGeneration.current++; readingAttachments.current = false;
    setAttachments([]); setReading(false); setAttachmentError(null);
    setMessages([]); setContext(emptyContext); setAnswer(null); setDraft(''); setError(null); setRetry(null); setAcknowledged(false);
    composer.current?.focus();
  };
  const submit = () => {
    if ((!draft.trim() && !attachments.length) || busy || retry || exhausted || readingAttachments.current) return;
    const next = [...messages, { role: 'user' as const, content: draft.trim(), ...(attachments.length ? { attachments } : {}) }];
    setDraft(''); setAttachments([]); setAttachmentError(null); void send(next);
  };
  const proposal = answer?.proposal;
  const exhausted = messages.length >= 29;
  const covered = contextTopics.filter(topic => context[topic.key]).length;
  return <div data-active={active} className={`vibe-check ${messages.length ? 'has-conversation' : 'is-welcome'}`}>
    {!introDismissed && <dialog ref={introDialog} className="vibe-check-intro" aria-labelledby="vibe-check-intro-title"
      onCancel={event => { event.preventDefault(); setIntroDismissed(true); }} onClose={() => { if (activeRef.current) setIntroDismissed(true); }}>
      <button type="button" className="vibe-check-intro-dismiss" aria-label="Dismiss introduction" onClick={() => setIntroDismissed(true)}>×</button>
      <h2 id="vibe-check-intro-title">Got writer's block?</h2>
      <p>No problem! Let's talk through your app idea and see if Vibes is a good fit.</p>
      <p className="vibe-check-scope-note">Vibe Check helps with app ideas for now. Have a skill in mind? Add it in the <a href="#/skills">Skills Catalog</a>.</p>
      <p>Or if you already know what you want, head on over to <a href="#/create" onClick={onBack ? event => { event.preventDefault(); onBack(); } : undefined}>Create an app</a>.</p>
      <div className="vibe-check-intro-footer">
        <label><input type="checkbox" checked={hideIntro} onChange={event => rememberIntro(event.target.checked)} /> Do not show again</label>
        <button type="button" className="vibe-check-intro-start" autoFocus onClick={() => setIntroDismissed(true)}>Let's go!</button>
      </div>
    </dialog>}
    <header className="vibe-check-topbar">
      <span style={eyebrowStyle}>Vibe Check</span>
      <div className="vibe-check-topbar-actions">
        <button type="button" className="vibe-check-text-button" disabled={busy} onClick={reset}>New conversation</button>
      </div>
    </header>
    {messages.length > 0 && <section className="vibe-check-progress" aria-label="Conversation progress">
      <div className="vibe-check-progress-heading"><span>{proposal ? 'Your plan is ready to review' : 'Getting to know your idea'}</span><span>{covered} of 5 topics covered</span></div>
      <div className="vibe-check-progress-track" role="progressbar" aria-label="Context gathered" aria-valuemin={0} aria-valuemax={5} aria-valuenow={covered} aria-valuetext={`${covered} of 5 topics covered`}><div style={{ width: `${covered * 20}%` }} /></div>
      <div className="vibe-check-progress-topics">{contextTopics.map(topic => <span key={topic.key} className={context[topic.key] ? 'covered' : ''}>{context[topic.key] ? '✓ ' : ''}{topic.label}</span>)}</div>
    </section>}
    <div className="vibe-check-workspace">
      {!messages.length && <div className="vibe-check-welcome">
        <h1>Let's do a Vibe Check</h1>
        <p>Big idea or just a hunch - let's talk through your app idea and shape a plan together.</p>
        <p className="vibe-check-scope-note">Vibe Check helps with app ideas for now. Have a skill in mind? Add it in the <a href="#/skills">Skills Catalog</a>.</p>
      </div>}
      {messages.length > 0 && <section className="vibe-check-thread" aria-label="Conversation" aria-live="polite">
      {messages.map((message, index) => {
        if (message.role === 'assistant' && index === messages.length - 1 && answer && answer.assessment !== 'exploring') return null;
        let content = message.content;
        if (message.role === 'assistant') { try { content = (JSON.parse(content) as VibeCheckResponse).message; } catch { /* plain text */ } }
        return <div className={`vibe-check-message ${message.role}`} key={index}><strong className="vibe-check-speaker">{message.role === 'user' ? 'You' : 'Vibe Check'}</strong>{message.attachments?.length ? <VibeCheckAttachments attachments={message.attachments} /> : null}{message.role === 'assistant' ? <AdvisorMessage content={content} /> : content && <p style={{ whiteSpace: 'pre-wrap' }}>{content}</p>}</div>;
      })}
      {busy && <VibeCheckThinking />}
      {error && <Alert variant="danger">{error}</Alert>}
      {retry && !busy && <div className="vibe-check-controls">
        <Button variant="secondary" onPress={() => void send(retry)}>Retry reply</Button>
        <Button variant="secondary" onPress={() => {
          const last = retry.at(-1)!;
          setMessages(retry.slice(0, -1)); setDraft(last.content); setAttachments(last.attachments ?? []);
          setRetry(null); setError(null); composer.current?.focus({ preventScroll: true });
        }}>Edit message</Button>
      </div>}
      {exhausted && <p>This conversation has reached its limit. Start a new Vibe Check to keep exploring.</p>}
    {answer && answer.assessment !== 'exploring' && <section className="vibe-check-summary" aria-label="App proposal" style={{ ...cardStyle, padding: 24 }}>
      <p className="vibe-check-print-heading">Vibes · Vibe Check</p>
      <section className="vibe-check-summary-section vibe-check-verdict" aria-label="Verdict">
        <p className="vibe-check-field-label">Verdict</p>
        <h2>{labels[answer.assessment]}</h2>
        <AdvisorMessage content={answer.message} />
      </section>
      <section className="vibe-check-summary-section" aria-label="Things to check">
        <h3>Things to check</h3>
        {answer.conditions.length > 0 ? <ul className="vibe-check-checklist">{answer.conditions.map(condition => <li key={condition}>{condition}</li>)}</ul>
          : <p>No additional requirements came up in this conversation. The admin team will still review the setup.</p>}
      </section>
      {proposal && <>
        <section className="vibe-check-summary-section" aria-label="Suggested application">
          <h3>Suggested app</h3>
          <dl className="vibe-check-app-fields">
            <div><dt>Suggested app name</dt><dd className="vibe-check-app-name">{proposal.appName}</dd></div>
            <div><dt>One-line description</dt><dd>{proposal.description}</dd></div>
          </dl>
        </section>
        <section className="vibe-check-summary-section" aria-label="Suggested capabilities to enable">
          <h3>Suggested capabilities to enable</h3>
          <div className="vibe-check-capabilities" role="list" aria-label="Recommended capabilities">
            <div className="vibe-check-capability" role="listitem"><div><h4>Web Application</h4><span className="vibe-check-capability-status">Included</span></div><p>An app people can open in their browser.</p></div>
            {capabilityDescriptions.filter(capability => proposal.services[capability.key]).map(capability =>
              <div className="vibe-check-capability" role="listitem" key={capability.key}><div><h4>{capability.name}</h4><span className="vibe-check-capability-status">Suggested</span></div><p>{capability.label}. {capability.detail}</p></div>)}
            {proposal.enableSecretsManagement && <div className="vibe-check-capability" role="listitem"><div><h4>Secrets Management</h4><span className="vibe-check-capability-status">Suggested</span></div><p>Keep connection details safe. Store the private keys or passwords used to connect to other tools.</p></div>}
          </div>
          {!capabilityDescriptions.some(capability => proposal.services[capability.key]) && !proposal.enableSecretsManagement && <p>The included web app is enough to start. No extra capabilities are suggested.</p>}
        </section>
        <details className="vibe-check-technical-details"><summary>Technical details</summary>
          <p>Language: {proposal.runtime === 'python' ? 'Python' : 'TypeScript'}</p>
          {proposal.services.cloudScheduler && <p>Schedule: {proposal.schedulerCron} · Pacific Time. You can adjust this during setup.</p>}
        </details>
        <p>Vibes will set up a starting point for your app. You’ll still need to build what it does, and the admin team will review the setup.</p>
        <div className="vibe-check-controls">
          {answer.assessment === 'conditional' && <label><input type="checkbox" checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} /> I understand these points need to be checked.</label>}
          <Button variant="primary" disabled={answer.assessment === 'conditional' && !acknowledged} onPress={() => onUse({ ...proposal, description: answer.conditions.length
            ? `${proposal.description}\n\nReview requirements:\n${answer.conditions.map(condition => `- ${condition}`).join('\n')}`
            : proposal.description })}>Use this setup</Button>
          <p>You can change this setup before sending your request.</p>
        </div></>}
      <p className="vibe-check-summary-note">Platform catalog {answer.catalogVersion}. This is a suggested plan. Access and setup still need to be checked.</p>
      {proposal && <div className="vibe-check-controls"><Button variant="secondary" onPress={() => window.print()}>Print / Save as PDF</Button></div>}
    </section>}
        <div ref={threadEnd} />
      </section>}
      <div className="vibe-check-composer-area">
        {attachmentError && <p className="vibe-check-attachment-error" role="alert">{attachmentError}</p>}
        <form className="vibe-check-composer" onSubmit={event => { event.preventDefault(); submit(); }}>
          {attachments.length > 0 && <VibeCheckAttachments attachments={attachments} onRemove={index => { setAttachments(current => current.filter((_, at) => at !== index)); setAttachmentError(null); }} />}
          <input ref={fileInput} type="file" multiple accept={ATTACHMENT_ACCEPT} aria-label="Upload reference files" hidden
            onChange={event => { const files = Array.from(event.target.files ?? []); event.target.value = ''; void attachFiles(files); }} />
          <label className="vibe-check-sr-only" htmlFor="vibe-check-message">Tell us about your idea</label>
          <textarea ref={composer} id="vibe-check-message" value={draft} maxLength={4000} rows={2}
            placeholder={messages.length ? 'Keep the conversation going…' : 'Tell me about your idea…'}
            disabled={exhausted}
            aria-busy={busy}
            onChange={event => setDraft(event.target.value)}
            onPaste={event => {
              const files = Array.from(event.clipboardData.items).filter(item => item.kind === 'file').map(item => item.getAsFile()).filter((file): file is File => file !== null);
              if (!files.length) return;
              event.preventDefault();
              void attachFiles(files.map(file => file.type.startsWith('image/') ? new File([file], `pasted-image-${Date.now()}.${file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1]}`, { type: file.type }) : file));
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); submit(); }
            }} />
          <div className="vibe-check-composer-footer">
            <div className="vibe-check-composer-tools">
              <button type="button" className="vibe-check-attach-button" aria-label="Attach files" title="Paste a screenshot or attach images, PDFs and text files. Kept only for this conversation." disabled={reading || exhausted || Boolean(retry)} onClick={() => fileInput.current?.click()}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m8 13 7-7a3 3 0 0 1 4 4L9 20a5 5 0 0 1-7-7L12 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Attach
              </button>
              <span>{reading ? 'Reading files…' : 'Enter to send · Shift + Enter for a new line'}</span>
            </div>
            <button type="submit" aria-label="Send" disabled={busy || (!draft.trim() && !attachments.length) || Boolean(retry) || exhausted || reading}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </form>
        {!messages.length && <div className="vibe-check-starters" aria-label="Ideas to explore">
          {starters.map(starter => <button type="button" key={starter.title} onClick={() => { setDraft(starter.prompt); composer.current?.focus(); }}>{starter.title}</button>)}
        </div>}
      </div>
    </div>
  </div>;
}
